import React from 'react';
import { Input } from '../../../../shared/components/Input';
import { Select } from '../../../../shared/components/Select';
import { Checkbox } from '../../../../shared/components/Checkbox';
import { ModuleSourceTable } from './ModuleSourceTable';
import { PipeFitterPane } from './PipeFitterPane';
import { NDTPane } from './NDTPane';
import { ModuleDef, ModuleState, ModuleSourceRow, sumPipeCounts } from './moduleDefs';
import { TIER_EXTENDED, TIER_STORAGE_ONLY } from './materialCostModel';
import { ModulePriceBreakdownPanel } from './ModulePriceBreakdownPanel';
import { MaterialCostAssumptionsDrawer } from './MaterialCostAssumptionsDrawer';
import { CoatingRatesDrawer } from './CoatingRatesDrawer';
import { ModuleBreakdown } from '../../utils/pricingSheetData';

interface Props {
  def: ModuleDef;
  module: ModuleState;
  onChange: (patch: Partial<ModuleState>) => void;
  // The selected module's slice of the last Calculate Price result. Absent until
  // the rep calculates, in which case the panel shows a hint.
  breakdown?: ModuleBreakdown | null;
  // The active configuration's Material and NRE cost blocks, used to prefill the
  // per-proposal cost-assumptions drawer. Absent until the config loads.
  materialConfig?: Record<string, number>;
  nreConfig?: Record<string, number>;
  pipefitterConfig?: Record<string, number>;
  ndtConfig?: Record<string, number>;
  coatingConfig?: Record<string, number>;
}

// Service tier is the only field kept from the old cost-input panel. The generic
// cost rates (hourly rate, compute, storage, QA overhead, margin) live only in
// the Configuration screen; the backend reads them from config at calculate time.
const TIER_OPTIONS = [
  { value: TIER_EXTENDED, label: 'Extended (full)' },
  { value: TIER_STORAGE_ONLY, label: 'Storage only' },
];

const TIER_TOOLTIP =
  'Extended prices the full tracking service. Storage only prices just keeping the records, so it costs far less.';

const MATERIAL_ADDONS: { flag: string; label: string; tooltip: string }[] = [
  {
    flag: 'gas',
    label: 'Gas certificates',
    tooltip: 'Track gas certificates as well. Adds this document type to the scope.',
  },
  {
    flag: 'wire',
    label: 'Welding-wire certificates',
    tooltip: 'Track welding-wire certificates as well. Adds this document type to the scope.',
  },
  {
    flag: 'coatingMaterials',
    label: 'Coating materials',
    tooltip: 'Track coating-material records as well. Adds this document type to the scope.',
  },
];

const MaterialOptions: React.FC<Props> = ({ module, onChange }) => {
  const options = module.options ?? {};
  const setOption = (key: string, value: unknown) =>
    onChange({ options: { ...options, [key]: value } });

  return (
    <div className="space-y-3">
      {MATERIAL_ADDONS.map(({ flag, label, tooltip }) => (
        <Checkbox
          key={flag}
          id={`material-${flag}`}
          label={label}
          tooltip={tooltip}
          checked={Boolean(options[flag])}
          onChange={(e) => setOption(flag, e.target.checked)}
        />
      ))}
    </div>
  );
};


export const ModuleDetailPane: React.FC<Props> = ({
  def,
  module,
  onChange,
  breakdown,
  materialConfig,
  nreConfig,
  coatingConfig,
  pipefitterConfig,
  ndtConfig,
}) => {
  const [costDrawerOpen, setCostDrawerOpen] = React.useState(false);

  // PipeFitter and NDT have their own panes, not the generic source-table layout.
  // Returned after the hook above so the hook order stays stable.
  if (def.key === 'pipeFitter') {
    return (
      <PipeFitterPane
        module={module}
        onChange={onChange}
        breakdown={breakdown}
        pipefitterConfig={pipefitterConfig}
        nreConfig={nreConfig}
      />
    );
  }
  if (def.key === 'ndt') {
    return (
      <NDTPane
        def={def}
        module={module}
        onChange={onChange}
        breakdown={breakdown}
        ndtConfig={ndtConfig}
        nreConfig={nreConfig}
      />
    );
  }

  const sources = module.sources ?? [];

  const overrides = module.overrides ?? {};
  const setOverride = (key: string, value: number | undefined) => {
    const next = { ...overrides };
    if (value === undefined || Number.isNaN(value)) delete next[key];
    else next[key] = value;
    onChange({ overrides: next });
  };
  const overrideCount = Object.keys(overrides).length;

  // Material Traceability (pipes) and Coating (joints) derive the total volume
  // from the per-source counts, so the per-source charge and the volume-present
  // validation stay in step.
  const isMaterial = def.key === 'materialTraceability';
  const isCoating = def.key === 'coating';
  const derivesVolume = isMaterial || isCoating;
  const volumeNoun = isCoating ? 'joints' : 'pipes';
  const derivedVolume = derivesVolume ? sumPipeCounts(sources) : undefined;
  const volumeIsDerived = derivesVolume && derivedVolume !== undefined;
  const volumeValue = volumeIsDerived ? derivedVolume : module.volume;

  const handleSourcesChange = (next: ModuleSourceRow[]) => {
    if (!derivesVolume) {
      onChange({ sources: next });
      return;
    }
    const nextVolume = sumPipeCounts(next);
    onChange(nextVolume === undefined ? { sources: next } : { sources: next, volume: nextVolume });
  };

  const tier = (module.tier as string) ?? TIER_EXTENDED;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-semibold text-blueprint-blue">{def.name}</h2>
          <p className="mt-1 text-sm text-heritage-grey">Onboarding sources and volume drive this module's price.</p>
        </div>
        {(isMaterial || isCoating) && (
          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={() => setCostDrawerOpen(true)}
              className="rounded-lg border border-[#E2E5EA] px-3 py-2 text-sm font-medium text-text-grey hover:bg-page-bg"
            >
              Edit cost assumptions
              {overrideCount > 0 && (
                <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">
                  {overrideCount}
                </span>
              )}
            </button>
            {isMaterial && (
              <div className="w-56">
                <Select
                  label="Service tier"
                  tooltip={TIER_TOOLTIP}
                  value={tier}
                  options={TIER_OPTIONS}
                  onChange={(value) => onChange({ tier: value })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {volumeIsDerived ? (
        <p className="text-sm font-medium text-text-grey">
          Total {volumeNoun}: {(volumeValue ?? 0).toLocaleString()}
          <span className="ml-2 font-normal text-heritage-grey">summed from the {def.rowNoun.toLowerCase()} rows below.</span>
        </p>
      ) : (
        <div className="w-48">
          <Input
            label={def.volumeLabel}
            tooltip="The volume this module prices against."
            type="number"
            min={0}
            value={volumeValue ?? ''}
            onChange={(e) =>
              onChange({ volume: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value) || 0) })
            }
            placeholder="0"
          />
        </div>
      )}

      <ModuleSourceTable
        def={def}
        sources={sources}
        onChange={handleSourcesChange}
      />

      {isMaterial && (
        <div className="border-t border-[#E2E5EA] pt-5">
          <h3 className="text-base font-medium text-blueprint-blue mb-3">Module options</h3>
          <MaterialOptions def={def} module={module} onChange={onChange} />
        </div>
      )}

      {isCoating && (
        <p className="text-sm text-heritage-grey">
          Each contractor sets its own capture mode and bolt-on hardware above. Joints coated drive the per-joint
          record charge. Platform and retention are added once at the project level.
        </p>
      )}

      <ModulePriceBreakdownPanel productId={def.key} breakdown={breakdown} />

      {isMaterial && (
        <MaterialCostAssumptionsDrawer
          open={costDrawerOpen}
          onClose={() => setCostDrawerOpen(false)}
          material={materialConfig ?? {}}
          nre={nreConfig ?? {}}
          overrides={overrides}
          onOverrideChange={setOverride}
          onResetAll={() => onChange({ overrides: {} })}
          discountDefault={
            (breakdown?.details as { discountPercentage?: number } | undefined)?.discountPercentage ?? 0
          }
        />
      )}

      {isCoating && (
        <CoatingRatesDrawer
          open={costDrawerOpen}
          onClose={() => setCostDrawerOpen(false)}
          config={coatingConfig}
          nreConfig={nreConfig}
          overrides={overrides}
          onOverrideChange={setOverride}
          onResetAll={() => onChange({ overrides: {} })}
          discountDefault={
            (breakdown?.details as { discountPercentage?: number } | undefined)?.discountPercentage ?? 0
          }
        />
      )}
    </div>
  );
};
